import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useWheelScrollProxy } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'
import type { SelectOption } from '#/shared/ui/select'
import { Skeleton } from '#/shared/ui/skeleton'

/** Dropdown list under the template search input. */
function OptionList({
  options,
  loading,
  emptyLabel,
  activeIndex,
  onHover,
  onPick,
}: {
  options: SelectOption[]
  loading?: boolean
  emptyLabel: string
  activeIndex: number
  onHover: (index: number) => void
  onPick: (value: string) => void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)
  useWheelScrollProxy(listRef)
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])
  return (
    <div
      ref={listRef}
      className="absolute top-full left-0 z-50 mt-1 flex max-h-60 w-full flex-col overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-popover"
    >
      {loading ? (
        <div className="px-2 py-1.5">
          <Skeleton className="h-4 w-full" />
        </div>
      ) : options.length === 0 ? (
        <div className="px-2 py-2 text-center text-sm text-gray-400">
          {emptyLabel}
        </div>
      ) : (
        options.map((option, index) => (
          <button
            key={option.value}
            ref={index === activeIndex ? activeRef : undefined}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(option.value)}
            className={cn(
              'flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-left text-sm text-gray-900',
              index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-100',
            )}
          >
            <span className="truncate">{option.label}</span>
          </button>
        ))
      )}
    </div>
  )
}

/** The resting state: the Figma pill that opens the search input on click. */
function PillButton({ label, onOpen }: { label: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-full border border-gray-200 bg-gray-100 px-3.5 text-sm text-gray-900"
    >
      <span className="truncate">{label}</span>
      <ChevronDownIcon className="size-2.5 shrink-0 text-gray-500" />
    </button>
  )
}

/** Template selector: a pill ("Select from templates" + chevron) that becomes a
 *  search input on click. Typing filters the options locally; ↑/↓/Enter navigate;
 *  picking applies it, then closes + blurs back to the pill. */
export function TemplatePicker({
  options,
  onSelect,
  loading,
}: {
  options: SelectOption[]
  onSelect?: (value: string) => void
  loading?: boolean
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options
  }, [options, query])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
  }, [])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Keep the highlight on the first match as the list narrows.
  useEffect(() => {
    setActive(0)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, close])

  const pick = (value: string) => {
    onSelect?.(value)
    inputRef.current?.blur()
    close()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') return close()
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (active < filtered.length) pick(filtered[active].value)
    }
  }

  if (!open) {
    return (
      <PillButton
        label={t('editor.phone.selectTemplate')}
        onOpen={() => setOpen(true)}
      />
    )
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="flex h-9 w-full items-center gap-2 rounded-full border border-gray-200 bg-gray-100 px-3.5 focus-within:focus-ring">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('editor.phone.searchTemplate')}
          className="h-full w-full min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
        <ChevronDownIcon className="size-2.5 shrink-0 text-gray-500" />
      </div>
      <OptionList
        options={filtered}
        loading={loading}
        emptyLabel={t('editor.phone.noTemplates')}
        activeIndex={active}
        onHover={setActive}
        onPick={pick}
      />
    </div>
  )
}
